import { toPositiveInteger } from 'modules/settings/components/custom-notifications/custom-notification-form.validation';
import {
    toThresholdFormState,
    toThresholdValue,
} from 'modules/settings/components/custom-notifications/custom-notification-threshold.mapper';
import {
    CatalogueTrigger,
    OperatorCustomNotification,
    TrackedTrigger,
} from 'modules/settings/pods/settings-operator-custom-notifications/operator-custom-notification.vm';
import { OperatorCustomNotificationWriteApi } from 'modules/settings/pods/settings-operator-custom-notifications/service/operator-custom-notifications.api';
import { toTriggerOptionId } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/operator-trigger-id.business';
import {
    ComparableOperatorCustomNotificationFormState,
    OperatorCustomNotificationFormState,
} from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notification-form/settings-operator-custom-notification-form.model';

export const mapOperatorCustomNotificationToFormState = (
    notification: OperatorCustomNotification
): OperatorCustomNotificationFormState => {
    const deviceTypeIds = [...new Set(notification.trackedTriggers.map((trigger) => trigger.deviceTypeId))];
    const triggerIds = notification.trackedTriggers.map((trigger) =>
        toTriggerOptionId(trigger.deviceTypeId, trigger.id)
    );

    return {
        name: notification.name,
        deviceTypes: { draftIds: deviceTypeIds, selectedIds: deviceTypeIds },
        triggers: { draftIds: triggerIds, selectedIds: triggerIds },
        windowDays: String(notification.windowDays),
        notifications: {
            gray: toThresholdFormState(notification.grayNotification),
            yellow: toThresholdFormState(notification.yellowNotification),
            red: toThresholdFormState(notification.redNotification),
        },
        isResettable: notification.isResettable,
        requiresCommentOnReset: notification.requiresCommentOnReset,
    };
};

export const toTrackedTrigger = ({ id, displayName, deviceTypeId }: CatalogueTrigger): TrackedTrigger => ({
    id,
    displayName,
    deviceTypeId,
});

const byTriggerIdentity = (first: TrackedTrigger, second: TrackedTrigger): number =>
    toTriggerOptionId(first.deviceTypeId, first.id).localeCompare(toTriggerOptionId(second.deviceTypeId, second.id));

export const buildOperatorCustomNotificationPayload = (
    form: OperatorCustomNotificationFormState,
    trackedTriggers: TrackedTrigger[]
): OperatorCustomNotificationWriteApi => ({
    name: form.name.trim(),
    eventsToTrack: trackedTriggers.toSorted(byTriggerIdentity),
    trackingTypeConfiguration: {
        type: 'SLIDING_WINDOW',
        windowDays: toPositiveInteger(form.windowDays) ?? 0,
    },
    grayNotification: toThresholdValue(form.notifications.gray),
    yellowNotification: toThresholdValue(form.notifications.yellow),
    redNotification: toThresholdValue(form.notifications.red),
    isResettable: form.isResettable,
    requiresCommentOnReset: form.requiresCommentOnReset,
});

export const mapComparableOperatorFormState = (
    form: OperatorCustomNotificationFormState
): ComparableOperatorCustomNotificationFormState => ({
    name: form.name,
    deviceTypeIds: form.deviceTypes.selectedIds.toSorted((first, second) => first.localeCompare(second)),
    triggerIds: form.triggers.selectedIds.toSorted((first, second) => first.localeCompare(second)),
    windowDays: form.windowDays,
    notifications: form.notifications,
    isResettable: form.isResettable,
    requiresCommentOnReset: form.requiresCommentOnReset,
});
